import { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import ProspectMap from "../components/ProspectMap";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { Checkbox } from "../components/ui/checkbox";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../components/ui/alert-dialog";
import { Loader2, Plus, Search, Contact, ShieldCheck, MapPin, List, Columns, X, Trash2, RefreshCw } from "lucide-react";
import { toast } from "sonner";

const EMPTY = { company_name: "", sede_nome: "", category: "", vat_number: "", tax_code: "", address: "", postal_code: "", city: "", province: "", region: "", country: "Italia", phone: "", email: "", website: "", primary_contact_name: "", primary_contact_role: "", notes: "", status: "Attivo" };
const CAT_OPTIONS = ["Impresa edile/General contractor", "Aziende", "Studi tecnici", "Privato"];
const PALETTE = ["#38bdf8", "#f59e0b", "#a78bfa", "#34d399", "#f472b6", "#f87171", "#60a5fa", "#FDD003", "#22d3ee", "#fb923c"];

export default function Clienti() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user?.role === "admin";
  const [items, setItems] = useState(null);
  const [stats, setStats] = useState(null);
  const [cfg, setCfg] = useState({ statuses: [] });
  const [geo, setGeo] = useState({ points: [], pending: 0 });
  const [view, setView] = useState("split");
  const [selected, setSelected] = useState(null);
  const [geoBusy, setGeoBusy] = useState(false);
  const [filters, setFilters] = useState({ search: "", category: "", status: "" });
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [syncing, setSyncing] = useState(false);

  const load = useCallback(() => {
    const p = new URLSearchParams();
    if (filters.search) p.set("q", filters.search);
    if (filters.category) p.set("category", filters.category);
    if (filters.status) p.set("status", filters.status);
    api.get(`/clienti?${p.toString()}`).then((r) => setItems(r.data.items)).catch(() => setItems([]));
    api.get(`/clienti/stats`).then((r) => setStats(r.data)).catch(() => {});
    api.get(`/clienti-geo`).then((r) => setGeo(r.data)).catch(() => setGeo({ points: [], pending: 0 }));
  }, [filters]);

  useEffect(() => { api.get("/clienti-config").then((r) => setCfg(r.data)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);

  const categories = useMemo(() => Object.keys(stats?.by_category || {}).filter((c) => c && c !== "—"), [stats]);
  const catList = useMemo(() => categories.map((c, i) => ({ key: c, label: c, color: PALETTE[i % PALETTE.length] })), [categories]);
  const catColor = (c) => catList.find((x) => x.key === c)?.color || "#9CA3AF";

  const geoPoints = useMemo(() => {
    const ids = new Set((items || []).map((c) => c.id));
    return (geo.points || []).filter((pt) => ids.has(pt.id));
  }, [geo, items]);

  const createCliente = async () => {
    if (!form.company_name.trim()) { toast.error("Ragione sociale / cognome obbligatorio"); return; }
    if (!form.address.trim() || !form.city.trim()) { toast.error("Indirizzo e Città sono obbligatori (per la geolocalizzazione sulla mappa)"); return; }
    setSaving(true);
    try {
      await api.post("/clienti", form);
      toast.success("Anagrafica creata");
      setShowNew(false); setForm(EMPTY); load();
    } catch (e) {
      if (e.response?.status === 409) toast.error("Cliente già presente (duplicato)");
      else toast.error(formatApiError(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const runGeocoding = async () => {
    setGeoBusy(true);
    try {
      let remaining = 1;
      while (remaining > 0) {
        const { data } = await api.post("/clienti-geo/geocode-pending?limit=20");
        remaining = data.remaining;
        if (data.processed === 0) break;
      }
      toast.success("Geolocalizzazione completata"); load();
    } catch { toast.error("Geolocalizzazione interrotta"); } finally { setGeoBusy(false); }
  };

  const toggleOne = (id) => setSelectedIds((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);
  const allSelected = (items || []).length > 0 && (items || []).every((c) => selectedIds.includes(c.id));
  const toggleAll = () => setSelectedIds(allSelected ? [] : (items || []).map((c) => c.id));

  const doDelete = async () => {
    if (!confirmDelete) return;
    setDeleting(true);
    try {
      if (confirmDelete.type === "single") {
        await api.delete(`/clienti/${confirmDelete.id}`);
        toast.success("Cliente eliminato");
      } else {
        const { data } = await api.post("/clienti/bulk-delete", { ids: selectedIds });
        toast.success(`${data.deleted} clienti eliminati`);
        setSelectedIds([]);
      }
      setConfirmDelete(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Eliminazione non riuscita"); }
    finally { setDeleting(false); }
  };

  return (
    <div className="p-4 lg:p-8 space-y-5" data-testid="clienti-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-heading font-bold flex items-center gap-2"><Contact className="h-6 w-6" style={{ color: "#FDD003" }} /> Anagrafiche</h1>
          <p className="text-sm text-muted-foreground">Anagrafiche clienti e potenziali, sincronizzate da FIER. {stats ? `${stats.total} anagrafiche · ${stats.enriched} arricchiti Creditsafe` : ""}{geo.pending > 0 ? ` · ${geo.pending} da geolocalizzare` : ""}</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {isAdmin && selectedIds.length > 0 && <Button variant="destructive" size="sm" onClick={() => setConfirmDelete({ type: "bulk" })} data-testid="clienti-bulk-delete-btn"><Trash2 className="h-4 w-4 mr-1.5" /> Elimina selezionati ({selectedIds.length})</Button>}
          {isAdmin && <Button variant="outline" size="sm" disabled={syncing} onClick={async () => {
            setSyncing(true);
            try {
              await api.post("/clienti/sync-fier");
              toast.info("Sincronizzazione FIER avviata: import anagrafiche, indirizzi e geolocalizzazione in corso (alcuni minuti).");
              const poll = setInterval(async () => {
                try {
                  const { data } = await api.get("/clienti/sync-fier/status");
                  if (data && data.phase === "done") {
                    clearInterval(poll); setSyncing(false);
                    toast.success(`FIER: ${data.created} nuove · ${data.updated} aggiornate · ${data.total} totali · geolocalizzazione automatica in corso`);
                    load();
                  } else if (data && data.phase === "error") {
                    clearInterval(poll); setSyncing(false);
                    toast.error(`Sincronizzazione FIER fallita: ${data.error || "errore"}`);
                  }
                } catch { /* keep polling */ }
              }, 5000);
            } catch (e) { toast.error(formatApiError(e)); setSyncing(false); }
          }} data-testid="clienti-sync-fier-btn">{syncing ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-1.5" />} Sincronizza con FIER</Button>}
          <Button size="sm" onClick={() => setShowNew(true)} data-testid="cliente-new-btn"><Plus className="h-4 w-4 mr-1.5" /> Nuova Anagrafica</Button>
        </div>
      </div>

      <Card className="rounded border-border p-4 space-y-3">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          <div className="relative col-span-2 md:col-span-1"><Search className="h-3.5 w-3.5 absolute left-2.5 top-3 text-muted-foreground" /><Input placeholder="Cerca..." value={filters.search} onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))} className="pl-8" data-testid="clienti-search" /></div>
          <Select value={filters.category || "all"} onValueChange={(v) => setFilters((f) => ({ ...f, category: v === "all" ? "" : v }))}><SelectTrigger data-testid="clienti-filter-category"><SelectValue placeholder="Tipologia" /></SelectTrigger><SelectContent><SelectItem value="all">Tutte le tipologie</SelectItem>{categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select>
          <Select value={filters.status || "all"} onValueChange={(v) => setFilters((f) => ({ ...f, status: v === "all" ? "" : v }))}><SelectTrigger data-testid="clienti-filter-status"><SelectValue placeholder="Stato" /></SelectTrigger><SelectContent><SelectItem value="all">Tutti gli stati</SelectItem>{(cfg.statuses || []).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select>
          <div className="flex items-center gap-1 justify-end">
            {geo.pending > 0 && <Button variant="outline" size="sm" onClick={runGeocoding} disabled={geoBusy} data-testid="clienti-geocode-btn">{geoBusy ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <MapPin className="h-3.5 w-3.5 mr-1.5" />} Geolocalizza ({geo.pending})</Button>}
            <Button variant={view === "list" ? "default" : "ghost"} size="icon" onClick={() => setView("list")} data-testid="clienti-view-list"><List className="h-4 w-4" /></Button>
            <Button variant={view === "split" ? "default" : "ghost"} size="icon" onClick={() => setView("split")} data-testid="clienti-view-split"><Columns className="h-4 w-4" /></Button>
            <Button variant={view === "map" ? "default" : "ghost"} size="icon" onClick={() => setView("map")} data-testid="clienti-view-map"><MapPin className="h-4 w-4" /></Button>
          </div>
        </div>
        {catList.length > 0 && (
          <div className="flex items-center gap-3 flex-wrap text-xs text-muted-foreground">
            {catList.map((c) => <span key={c.key} className="flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-full" style={{ background: c.color }} /> {c.label}</span>)}
          </div>
        )}
      </Card>

      <div className={`grid gap-4 ${view === "split" ? "lg:grid-cols-2" : "grid-cols-1"}`}>
        {view !== "map" && (
          <Card className="rounded border-border overflow-x-auto" data-testid="clienti-table">
            {!items ? <div className="flex h-40 items-center justify-center"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>
              : items.length === 0 ? <p className="p-10 text-center text-sm text-muted-foreground">Nessun cliente.</p>
                : <Table><TableHeader><TableRow className="hover:bg-transparent border-border">{isAdmin && <TableHead className="w-8"><Checkbox checked={allSelected} onCheckedChange={toggleAll} data-testid="clienti-select-all" /></TableHead>}<TableHead className="text-xs uppercase">Ragione sociale / Cognome</TableHead><TableHead className="text-xs uppercase">Tipologia</TableHead><TableHead className="text-xs uppercase">Comune</TableHead><TableHead className="text-xs uppercase">Stato</TableHead><TableHead className="text-xs uppercase text-right">Creditsafe</TableHead>{isAdmin && <TableHead className="w-8" />}</TableRow></TableHeader>
                  <TableBody>{items.map((c) => (
                    <TableRow key={c.id} onClick={() => navigate(`/clienti/${c.id}`)} data-testid={`cliente-row-${c.id}`} className="cursor-pointer border-border">
                      {isAdmin && <TableCell onClick={(e) => e.stopPropagation()}><Checkbox checked={selectedIds.includes(c.id)} onCheckedChange={() => toggleOne(c.id)} data-testid={`cliente-select-${c.id}`} /></TableCell>}
                      <TableCell className="font-medium">{c.company_name}{c.sede_nome ? <span className="text-muted-foreground font-normal"> · {c.sede_nome}</span> : ""}</TableCell>
                      <TableCell><span className="inline-flex items-center gap-1.5 text-sm"><span className="h-2 w-2 rounded-full" style={{ background: catColor(c.category) }} />{c.category || "—"}</span></TableCell>
                      <TableCell className="text-sm text-muted-foreground">{c.city}{c.province ? ` (${c.province})` : ""}</TableCell>
                      <TableCell><span className="text-xs">{c.status}</span></TableCell>
                      <TableCell className="text-right">{c.creditsafe ? <span className="inline-flex items-center gap-1 text-xs text-emerald-500"><ShieldCheck className="h-3.5 w-3.5" /> {c.creditsafe.risk_band || "OK"}</span> : <span className="text-xs text-muted-foreground">—</span>}</TableCell>
                      {isAdmin && <TableCell onClick={(e) => e.stopPropagation()}><button onClick={() => setConfirmDelete({ type: "single", id: c.id, name: c.company_name })} data-testid={`cliente-delete-${c.id}`} className="text-muted-foreground hover:text-red-500 transition-colors"><Trash2 className="h-4 w-4" /></button></TableCell>}
                    </TableRow>))}
                  </TableBody></Table>}
          </Card>
        )}
        {view !== "list" && (
          <div className="relative">
            <ProspectMap points={geoPoints} categories={catList} onSelect={setSelected} height={view === "split" ? 560 : 620} />
            {selected && (
              <Card className="absolute top-3 right-3 w-72 rounded border-border p-4 shadow-xl bg-card z-10" data-testid="clienti-map-card">
                <button onClick={() => setSelected(null)} className="absolute top-2 right-2 text-muted-foreground"><X className="h-4 w-4" /></button>
                <div className="font-heading font-semibold text-sm pr-5">{selected.company_name}</div>
                <div className="text-xs text-muted-foreground mt-0.5">{selected.category || "—"} · {selected.city}{selected.province ? ` (${selected.province})` : ""}</div>
                <div className="text-xs mt-2 space-y-0.5">
                  {selected.primary_contact_name && <div>{selected.primary_contact_name}</div>}
                  {selected.phone && <div>{selected.phone}</div>}
                  {selected.risk_band && <div>Rating Creditsafe: <b>{selected.risk_band}</b>{selected.score != null ? ` (${selected.score}/100)` : ""}</div>}
                  <div>Stato: <b>{selected.status}</b></div>
                </div>
                <Button size="sm" className="w-full mt-3" onClick={() => navigate(`/clienti/${selected.id}`)} data-testid="clienti-map-open">Apri scheda</Button>
              </Card>
            )}
          </div>
        )}
      </div>

      <Dialog open={showNew} onOpenChange={setShowNew}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="cliente-new-dialog">
          <DialogHeader><DialogTitle>Nuova Anagrafica</DialogTitle><DialogDescription>Inserisci i dati dell'anagrafica.</DialogDescription></DialogHeader>
          <div className="grid sm:grid-cols-2 gap-3">
            <Fld label="Ragione sociale / Cognome *"><Input value={form.company_name} onChange={(e) => setForm((f) => ({ ...f, company_name: e.target.value }))} data-testid="nc-company" /></Fld>
            <Fld label="Sede / Nome"><Input value={form.sede_nome} onChange={(e) => setForm((f) => ({ ...f, sede_nome: e.target.value }))} /></Fld>
            <Fld label="Tipologia"><Select value={form.category || undefined} onValueChange={(v) => setForm((f) => ({ ...f, category: v }))}><SelectTrigger data-testid="nc-category"><SelectValue placeholder="Seleziona..." /></SelectTrigger><SelectContent>{CAT_OPTIONS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select></Fld>
            <Fld label="Stato"><Select value={form.status} onValueChange={(v) => setForm((f) => ({ ...f, status: v }))}><SelectTrigger data-testid="nc-status"><SelectValue /></SelectTrigger><SelectContent>{(cfg.statuses || []).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select></Fld>
            <Fld label="Partita IVA"><Input value={form.vat_number} onChange={(e) => setForm((f) => ({ ...f, vat_number: e.target.value }))} data-testid="nc-vat" /></Fld>
            <Fld label="Codice fiscale"><Input value={form.tax_code} onChange={(e) => setForm((f) => ({ ...f, tax_code: e.target.value }))} /></Fld>
            <Fld label="Indirizzo *" className="sm:col-span-2"><Input value={form.address} onChange={(e) => setForm((f) => ({ ...f, address: e.target.value }))} data-testid="nc-address" /></Fld>
            <Fld label="CAP"><Input value={form.postal_code} onChange={(e) => setForm((f) => ({ ...f, postal_code: e.target.value }))} /></Fld>
            <Fld label="Comune *"><Input value={form.city} onChange={(e) => setForm((f) => ({ ...f, city: e.target.value }))} data-testid="nc-city" /></Fld>
            <Fld label="Provincia"><Input value={form.province} onChange={(e) => setForm((f) => ({ ...f, province: e.target.value }))} /></Fld>
            <Fld label="Telefono"><Input value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} /></Fld>
            <Fld label="Email"><Input value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} /></Fld>
            <Fld label="Referente"><Input value={form.primary_contact_name} onChange={(e) => setForm((f) => ({ ...f, primary_contact_name: e.target.value }))} /></Fld>
          </div>
          <div className="flex justify-end gap-2 pt-2"><Button variant="outline" onClick={() => setShowNew(false)}>Annulla</Button><Button onClick={createCliente} disabled={saving} data-testid="nc-save">{saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Crea</Button></div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent data-testid="clienti-delete-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Conferma eliminazione</AlertDialogTitle>
            <AlertDialogDescription>
              {confirmDelete?.type === "single" ? `Eliminare definitivamente "${confirmDelete?.name}"?` : `Eliminare definitivamente ${selectedIds.length} clienti selezionati?`} L'operazione non è reversibile.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={(e) => { e.preventDefault(); doDelete(); }} disabled={deleting} className="bg-red-600 hover:bg-red-700" data-testid="clienti-delete-confirm-btn">{deleting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Elimina</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function Fld({ label, children, className = "" }) {
  return <div className={`space-y-1.5 ${className}`}><Label className="text-xs uppercase tracking-widest text-muted-foreground">{label}</Label>{children}</div>;
}
